/**
 * Card da extensão Teleconsultoria — espelho do card de Pronto Atendimento.
 * Contract identifier: "teleconsultoria"
 */
import {
  Stack,
  Typography,
  Button,
  useTheme,
  Divider,
  Box,
} from "@mui/material";
import { PrimaryButton } from "@/components/common";
import { ReactNode, useState } from "react";
import { useTeleconsultoriaContractContext } from "@/contexts/TeleconsultoriaContractContext";
import { PostContract } from "@/services/fhir/contract/PostContract";
import { PatchContract } from "@/services/fhir/contract/PatchContract";
import { toast } from "react-toastify";
import { useSessionContext } from "@/contexts/SessionContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { MedicalServices } from "@mui/icons-material";
import { HealthPlanModals } from "./HealthPlanModals";
import dayjs from "dayjs";
import { useFormatter } from "@/utils/useFormatter";
import { deactivateOrganizationTeleconsultoria } from "src-next/modules/teleconsultoria/services/deactivateOrganizationTeleconsultoria";
import { organizationTokenFromId } from "src-next/modules/teleconsultoria/services/teleconsultoriaScheduleService";

interface TeleconsultoriaExtensionCardProps {
  title: string;
  description: string;
  status?: string;
  children?: ReactNode;
  priceInfo?: string;
}

export function TeleconsultoriaExtensionCard({
  title,
  description,
  status,
  children,
  priceInfo,
}: TeleconsultoriaExtensionCardProps) {
  const { access, user } = useSessionContext();
  const { organization } = useOrganizationContext();
  const theme = useTheme();
  const { contractTeleconsultoria, refetchContract } =
    useTeleconsultoriaContractContext();
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenActivateModal = () => {
    setIsActivateModalOpen(true);
  };

  const handleCloseActivateModal = () => {
    setIsActivateModalOpen(false);
  };

  const handleOpenCancelModal = () => {
    setIsCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    setIsCancelModalOpen(false);
  };

  const handleActivate = async () => {
    setIsLoading(true);
    try {
      // Gera data no fuso horário local subtraindo 3 horas (UTC-3) — mesmo TISS
      const currentDate = dayjs().subtract(3, "hour").toISOString();

      await PostContract({
        data: {
          resourceType: "Contract",
          extension: [
            {
              url: "https://fluxmed.com.br/fhir/extension/teleconsultoria/cpf",
              valueString: user?.username || "",
            },
            {
              url: "https://fluxmed.com.br/fhir/extension/teleconsultoria/nome",
              valueString: user?.name || "",
            },
            {
              url: "https://fluxmed.com.br/fhir/extension/teleconsultoria/email",
              valueString: user?.email || "",
            },
          ],
          identifier: [
            {
              system: "https://fluxmed.com.br/hapi/fhir/R5/contract-types",
              // Identifier estável da extensão Teleconsultoria
              value: "teleconsultoria",
            },
          ],
          status: "executed",
          authority: [
            {
              reference: `Organization/${organization?.id}`,
            },
          ],
          type: {
            coding: [
              {
                system: "https://fluxmed.com.br/hapi/fhir/R5/contract-types",
                code: "teleconsultoria",
              },
            ],
          },
          term: [
            {
              identifier: {
                system: "https://fluxmed.com.br/hapi/fhir/R5/contract-terms",
                value: "teleconsultoria",
              },
              issued: currentDate,
              applies: {
                start: currentDate,
              },
              text: "Contrato de extensão para utilização do Teleconsultoria",
              offer: {
                type: {
                  coding: [
                    {
                      system:
                        "https://fluxmed.com.br/hapi/fhir/R5/contract-offer-types",
                      code: "subscription",
                    },
                  ],
                },
              },
              asset: [
                {
                  valuedItem: [
                    {
                      entityCodeableConcept: {
                        coding: [
                          {
                            system:
                              "https://fluxmed.com.br/hapi/fhir/R5/subscription-types",
                            code: "subscription-fee",
                          },
                        ],
                      },
                      quantity: {
                        value: 1,
                        unit: "subscription",
                      },
                      unitPrice: {
                        value: 0,
                        currency: "BRL",
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      });

      toast.success("Extensão de Teleconsultoria ativada com sucesso!");
      await refetchContract();
      setIsActivateModalOpen(false);
    } catch (error) {
      console.error("Erro ao ativar extensão de Teleconsultoria:", error);
      toast.error("Erro ao ativar extensão. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    setIsLoading(true);
    // Agendas primeiro: se falhar, o contrato não é cancelado e o usuário pode tentar de novo.
    try {
      await deactivateOrganizationTeleconsultoria(
        organizationTokenFromId(organization?.id),
      );
    } catch (error) {
      console.error("Erro ao desativar agendas de Teleconsultoria:", error);
      toast.error(
        "Não foi possível desativar as agendas de teleconsultoria. A extensão não foi cancelada.",
      );
      setIsLoading(false);
      return;
    }
    try {
      await PatchContract({
        contractId: contractTeleconsultoria?.id,
        operation: "replace",
        path: "/status",
        content: "cancelled",
      });

      toast.success("Extensão de Teleconsultoria cancelada com sucesso!");
      await refetchContract();
      setIsCancelModalOpen(false);
    } catch (error) {
      console.error("Erro ao cancelar extensão de Teleconsultoria:", error);
      toast.error("Erro ao cancelar extensão. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Stack
        sx={{
          border: "1px solid",
          borderColor: "neutral700.main",
          borderRadius: "15px",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
          minHeight: "400px",
          paddingTop: 4,
          paddingBottom: 3,
        }}
      >
        {priceInfo && (
          <Stack
            sx={{
              position: "absolute",
              top: "12px",
              left: "0px",
              bgcolor: "secondary800.main",
              color: "white",
              padding: "4px 12px",
              borderTopRightRadius: "15px",
              borderBottomRightRadius: "15px",
              fontSize: "12px",
              fontWeight: "bold",
              zIndex: 1,
            }}
          >
            {priceInfo}
          </Stack>
        )}

        {status && (
          <Stack
            sx={{
              position: "absolute",
              top: "12px",
              right: "0px",
              bgcolor:
                status === "Em desenvolvimento"
                  ? "warning.main"
                  : "success.main",
              color: "white",
              padding: "4px 12px",
              borderTopLeftRadius: "15px",
              borderBottomLeftRadius: "15px",
              fontSize: "12px",
              fontWeight: "bold",
              zIndex: 1,
            }}
          >
            {status}
          </Stack>
        )}

        <Stack
          direction="row"
          width="100%"
          justifyContent="center"
          marginTop="auto"
          paddingTop={2}
        >
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: "500",
              paddingBottom: 2,
              paddingLeft: 5,
              paddingRight: 5,
              paddingTop: 1,
              alignSelf: "center",
              textAlign: "center",
              position: "relative",
              zIndex: 0,
            }}
          >
            {title}
          </Typography>
        </Stack>

        <Stack flexGrow={1} overflow="auto" maxHeight="330px">
          <Typography
            variant="body1"
            color="neutral700.main"
            sx={{
              paddingBottom: 3,
              paddingLeft: 5,
              paddingRight: 5,
            }}
            align="left"
          >
            {description}
          </Typography>

          {children && (
            <Stack paddingLeft={5} paddingRight={5} paddingBottom={3}>
              {children}
            </Stack>
          )}

          {contractTeleconsultoria && (
            <Stack paddingLeft={5} paddingRight={5} paddingBottom={3}>
              <Divider sx={{ marginY: 2 }} />
              <Typography
                variant="body1"
                fontWeight="600"
                textAlign="center"
                gutterBottom
              >
                Responsável pela ativação
              </Typography>
              <Box
                sx={{
                  backgroundColor: "neutral100.main",
                  p: 2,
                  borderRadius: 2,
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight="500"
                  sx={{ mb: 1, wordWrap: "break-word", overflow: "hidden" }}
                >
                  <strong>Nome:</strong>{" "}
                  {contractTeleconsultoria?.extension?.find((e) =>
                    e.url?.includes("nome"),
                  )?.valueString || "-"}
                </Typography>
                <Typography variant="body1" fontWeight="500" sx={{ mb: 1 }}>
                  <strong>CPF:</strong>{" "}
                  {useFormatter?.formatCPF(
                    contractTeleconsultoria?.extension?.find((e) =>
                      e.url?.includes("cpf"),
                    )?.valueString || "",
                  ) || "-"}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="500"
                  sx={{ wordWrap: "break-word", overflow: "hidden" }}
                >
                  <strong>Email:</strong>{" "}
                  {contractTeleconsultoria?.extension?.find((e) =>
                    e.url?.includes("email"),
                  )?.valueString || "-"}
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>

        <Stack
          direction="row"
          width="100%"
          justifyContent="center"
          marginTop="auto"
          paddingTop={2}
        >
          {contractTeleconsultoria ? (
            <Button
              sx={{
                width: "70%",
                height: "40px",
                backgroundColor: access?.roles?.includes("owner")
                  ? "error700.main"
                  : "neutral300.main",
                color: "neutral0.main",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: access?.roles?.includes("owner")
                    ? "error700.main"
                    : "neutral300.main",
                },
                textTransform: "capitalize",
                fontSize: theme.typography.subtitle2.fontSize,
                "&:disabled": {
                  color: "neutral600.main",
                },
              }}
              startIcon={<span style={{ color: "white" }}>⚠️</span>}
              disabled={!access?.roles?.includes("owner")}
              onClick={handleOpenCancelModal}
            >
              {access?.roles?.includes("owner")
                ? "Cancelar Teleconsultoria"
                : "Somente proprietários"}
            </Button>
          ) : (
            <PrimaryButton
              width="70%"
              height="40px"
              onClick={handleOpenActivateModal}
              startIcon={<MedicalServices />}
              // Mesmo bloqueio de produção da extensão TISS
              disabled={
                !access?.roles?.includes("owner")
              }
            >
              {access?.roles?.includes("owner")
                ? "Ativar Teleconsultoria"
                : "Somente proprietários"}
            </PrimaryButton>
          )}
        </Stack>
      </Stack>

      {/* Reutiliza modais genéricos da TISS (só o agentName muda) */}
      <HealthPlanModals
        isActivateModalOpen={isActivateModalOpen}
        isCancelModalOpen={isCancelModalOpen}
        isLoading={isLoading}
        onCloseActivateModal={handleCloseActivateModal}
        onCloseCancelModal={handleCloseCancelModal}
        onConfirmActivate={handleActivate}
        onConfirmCancel={handleCancel}
        agentName={title}
        cancelWarning="Ao cancelar, todas as agendas de teleconsultoria desta organização serão desativadas e os profissionais deixarão de receber convites."
      />
    </>
  );
}
